import { Fragment } from 'react'

import {
  ActiveContext,
  ReportDataIssueConfig,
  ReportReason,
} from './CopReportDataIssue.config'

type MainPopupFooterProps = {
  showSuccess: boolean
  activeContext: ActiveContext
  config: ReportDataIssueConfig
  isSubmitting: boolean
  onCloseAll: () => void
  onRequestClose: () => void
  onSubmit: () => void
}

export function MainPopupFooter({
  showSuccess,
  activeContext,
  config,
  isSubmitting,
  onCloseAll,
  onRequestClose,
  onSubmit,
}: Readonly<MainPopupFooterProps>) {
  if (showSuccess) {
    return (
      <button
        type="button"
        className="cop-report-data-issue__btn cop-report-data-issue__btn--primary"
        onClick={onCloseAll}
      >
        {activeContext.type === 'asset'
          ? config.confirmation?.backToAssetLabel || ''
          : config.confirmation?.backToSiteLabel || ''}
      </button>
    )
  }

  return (
    <Fragment>
      <button
        type="button"
        className="cop-report-data-issue__btn cop-report-data-issue__btn--secondary"
        onClick={onRequestClose}
        disabled={isSubmitting}
      >
        {config.cancelButtonLabel || ''}
      </button>
      <button
        type="button"
        className="cop-report-data-issue__btn cop-report-data-issue__btn--primary"
        onClick={onSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? '...' : config.sendButtonLabel || ''}
      </button>
    </Fragment>
  )
}

type MainPopupContentProps = {
  showSuccess: boolean
  activeContext: ActiveContext
  config: ReportDataIssueConfig
  reasons: ReportReason[]
  selectedReasonKeys: string[]
  reasonError: string
  description: string
  descriptionError: string
  maxDescriptionLength: number
  onToggleReason: (key: string) => void
  onDescriptionChange: (value: string) => void
}

export function MainPopupContent({
  showSuccess,
  activeContext,
  config,
  reasons,
  selectedReasonKeys,
  reasonError,
  description,
  descriptionError,
  maxDescriptionLength,
  onToggleReason,
  onDescriptionChange,
}: Readonly<MainPopupContentProps>) {
  if (showSuccess) {
    return (
      <div className="cop-report-data-issue__confirmation">
        <p className="cop-report-data-issue__text">
          {config.confirmation?.message || `Thank you for submitting the report about your ${activeContext.type}:`}
          {activeContext.name && (
            <span className="cop-report-data-issue__context">{activeContext.name}</span>
          )}
        </p>
        <p className="cop-report-data-issue__text">
          {config.confirmation?.nextStepsLabel || ''}
        </p>
        <ul className="cop-report-data-issue__list">
          <li>{config.confirmation?.nextStep1 || ''}</li>
          <li>{config.confirmation?.nextStep2 || ''}</li>
        </ul>
        <p className="cop-report-data-issue__text">
          {config.confirmation?.contactUsLabel || ''}{' '}
          <a href={`mailto:${config.confirmation?.contactEmail || ''}`} className="cop-report-data-issue__email">
            {config.confirmation?.contactEmail || ''}
          </a>
        </p>
      </div>
    )
  }

  return (
    <div className="cop-report-data-issue__form">
      <p className="cop-report-data-issue__label">
        {config.reasonsLabel || ''}
      </p>
      <div className="cop-report-data-issue__reasons">
        {reasons.map(reason => {
          const inputId = `cop-report-data-issue-${reason.key}`
          const checked = selectedReasonKeys.includes(reason.key)
          return (
            <label key={reason.key} htmlFor={inputId} className="cop-report-data-issue__reason-item">
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={() => onToggleReason(reason.key)}
              />
              <span>{reason.value}</span>
            </label>
          )
        })}
      </div>
      {reasonError && <p className="cop-report-data-issue__error">{reasonError}</p>}

      <p className="cop-report-data-issue__label">
        {config.descriptionLabel || ''}
      </p>
      <div className="cop-report-data-issue__textarea-wrap">
        <textarea
          className={`cop-report-data-issue__textarea${descriptionError ? ' cop-report-data-issue__textarea--error' : ''}`}
          placeholder={config.descriptionPlaceholder || ''}
          value={description}
          onChange={event => onDescriptionChange(event.target.value)}
          maxLength={maxDescriptionLength}
          rows={5}
        />
      </div>
      <span className="cop-report-data-issue__counter">{description.length}/{maxDescriptionLength}</span>
      {descriptionError && <p className="cop-report-data-issue__error">{descriptionError}</p>}
    </div>
  )
}

type UnsavedConfirmFooterProps = {
  config: ReportDataIssueConfig
  onConfirmClose: () => void
  onCancelClose: () => void
}

export function UnsavedConfirmFooter({
  config,
  onConfirmClose,
  onCancelClose,
}: Readonly<UnsavedConfirmFooterProps>) {
  return (
    <Fragment>
      <button
        type="button"
        className="cop-report-data-issue__btn cop-report-data-issue__btn--primary"
        onClick={onConfirmClose}
      >
        {config.unsavedChanges?.yesLabel || ''}
      </button>
      <button
        type="button"
        className="cop-report-data-issue__btn cop-report-data-issue__btn--secondary"
        onClick={onCancelClose}
      >
        {config.unsavedChanges?.noLabel || ''}
      </button>
    </Fragment>
  )
}